import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Sparkles,
  Zap,
  Crown,
  ArrowRight,
  TrendingUp,
  Shield,
} from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  PrimaryButton,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

/* ------------------------------------------------------------------ */
/*  CONTENT                                                            */
/* ------------------------------------------------------------------ */
const plans = [
  {
    code: "STARTER",
    name: "Starter",
    icon: Zap,
    accent: C.coral,
    monthly: 99,
    annual: 79,
    listMonthly: 149,
    blurb: "Everything a lean team needs to hire smart, without the enterprise price tag.",
    cta: "Start free trial",
    href: createPageUrl("Contact"),
    features: [
      "Up to 50 active job postings",
      "AI resume parsing",
      "Basic applicant tracking",
      "Email support",
      "5 team members",
      "Basic analytics",
    ],
  },
  {
    code: "PRO",
    name: "Professional",
    icon: TrendingUp,
    accent: C.mint,
    monthly: 299,
    annual: 239,
    listMonthly: 399,
    popular: true,
    blurb: "Built for teams hiring fast across roles, automation that pays for itself.",
    cta: "Book a demo",
    href: createPageUrl("Contact"),
    features: [
      "Unlimited job postings",
      "Advanced AI screening + ranking",
      "Full ATS + CRM integration",
      "Priority chat & email support",
      "15 team members",
      "Advanced analytics dashboards",
      "Custom workflow automations",
    ],
  },
  {
    code: "ENT",
    name: "Enterprise",
    icon: Crown,
    accent: C.gold,
    monthly: null,
    annual: null,
    blurb: "Your stack, your rules, global compliance, custom AI, and a team behind you.",
    cta: "Talk to sales",
    href: createPageUrl("Contact"),
    features: [
      "Everything in Professional",
      "Unlimited team members",
      "Custom AI fine-tuning",
      "Dedicated success manager",
      "SOC 2 + GDPR add-ons",
      "Multi-country payroll",
      "SLA-backed uptime",
    ],
  },
];

const compareRows = [
  { label: "Active job postings", values: ["50", "Unlimited", "Unlimited"] },
  { label: "Team members",         values: ["5",  "15",        "Unlimited"] },
  { label: "AI screening",         values: ["Basic", "Advanced", "Custom-tuned"] },
  { label: "Analytics",            values: ["Basic", "Advanced", "Custom dashboards"] },
  { label: "Workflow automation",  values: ["N/A",     "✓",        "✓ + custom"] },
  { label: "SSO / SAML",           values: ["N/A",     "Add-on",    "✓"] },
  { label: "Dedicated CSM",        values: ["N/A",     "N/A",         "✓"] },
  { label: "SOC 2 / GDPR",         values: ["N/A",     "Add-on",    "Included"] },
];

const faqs = [
  {
    q: "Can I switch plans later?",
    a: "Yes upgrade or downgrade any time. We pro-rate the bill, no surprises.",
  },
  {
    q: "What happens after the trial?",
    a: "Your data stays. You'll get a friendly reminder before any charge never an autobill ambush.",
  },
  {
    q: "Do you offer a discount for non-profits?",
    a: "We do 30% off Starter and Pro for verified non-profits and educational institutions.",
  },
];

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */
export default function Pricing() {
  const [billing, setBilling] = useState("monthly"); // "monthly" | "annual"

  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section
        className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight"
        style={{ borderColor: C.hairline }}
      >
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Pricing · 03 plans</StatusBadge>
            </motion.div>

            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] xl:text-[5.75rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Simple, honest
              <br />
              <span style={{ color: `${C.forest}80` }}>pricing.</span>
            </motion.h1>

            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              One bill, one login, the whole suite. Pick a tier to start switch
              anytime as your team grows.
            </motion.p>

            {/* Billing toggle, skeuomorphic pill */}
            <motion.div variants={motionFadeUp} custom={3} className="mt-10 inline-flex">
              <div
                className="inline-flex p-1 border"
                style={{
                  borderColor: C.hairline,
                  background: "linear-gradient(180deg, #f0f0ee 0%, #ffffff 18%, #ffffff 100%)",
                  boxShadow:
                    "inset 0 1px 0 rgba(0,0,0,0.04), inset 0 -1px 0 rgba(255,255,255,0.5)",
                }}
              >
                <ToggleButton active={billing === "monthly"} onClick={() => setBilling("monthly")}>
                  Monthly
                </ToggleButton>
                <ToggleButton active={billing === "annual"} onClick={() => setBilling("annual")}>
                  Annual
                  <span
                    className="ml-2 px-1.5 py-0.5 text-[9px] font-bold"
                    style={{
                      background: C.mint,
                      color: C.forest,
                      letterSpacing: "0.18em",
                    }}
                  >
                    -20%
                  </span>
                </ToggleButton>
              </div>
            </motion.div>
          </motion.div>
        </PageContainer>
      </section>

      {/* PLAN CARDS */}
      <section className="py-16 md:py-20 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          {/* Launch offer banner */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative mb-10 overflow-hidden border p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            style={{
              borderColor: C.forest,
              background: "linear-gradient(105deg, #1A3C2B 0%, #21503A 100%)",
              color: C.paper,
            }}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-11 h-11 shrink-0 flex items-center justify-center"
                style={{ background: C.mint, color: C.forest }}
              >
                <Sparkles className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <TechLabel style={{ color: C.mint }}>Founding customer pricing · limited time</TechLabel>
                </div>
                <p className="mt-1 text-lg md:text-xl font-bold leading-tight">
                  Join early and lock in these rates for life.
                </p>
                <p className="mt-1 text-sm" style={{ color: `${C.paper}b3` }}>
                  Prices rise as we ship more, sign up now and your rate never does. Start with a <span className="font-bold" style={{ color: C.paper }}>14-day free trial</span>, no credit card.
                </p>
              </div>
            </div>
            <div className="shrink-0">
              <span
                className="inline-flex items-center px-3 py-1.5 text-xs font-bold tabular-nums"
                style={{ background: C.mint, color: C.forest, letterSpacing: "0.1em" }}
              >
                SAVE UP TO 35%
              </span>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-7">
            {plans.map((p, i) => (
              <PlanCard key={p.code} plan={p} billing={billing} index={i} />
            ))}
          </div>

          <div className="mt-10 text-center">
            <TechLabel style={{ color: `${C.grid}99` }}>
              all plans include · 14-day free trial · no credit card · cancel anytime
            </TechLabel>
          </div>
        </PageContainer>
      </section>

      {/* COMPARE */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 max-w-2xl">
            <StatusBadge>Side-by-side</StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
              The full comparison.
            </h2>
          </div>

          <div className="border" style={{ borderColor: C.hairline, background: C.paper }}>
            {/* Header row */}
            <div
              className="grid grid-cols-4 gap-px border-b"
              style={{ borderColor: C.hairline, background: C.hairline }}
            >
              <div className="p-4" style={{ background: C.paper }}>
                <TechLabel style={{ color: `${C.grid}99` }}>feature</TechLabel>
              </div>
              {plans.map((p) => (
                <div key={p.code} className="p-4" style={{ background: C.paper }}>
                  <TechLabel style={{ color: p.accent }}>{p.name}</TechLabel>
                </div>
              ))}
            </div>

            {/* Rows */}
            {compareRows.map((row, idx) => (
              <div
                key={row.label}
                className="grid grid-cols-4 gap-px"
                style={{
                  background: C.hairline,
                  borderBottom: idx < compareRows.length - 1 ? `1px solid ${C.hairline}` : "none",
                }}
              >
                <div className="p-4 text-sm" style={{ background: C.paper, color: C.grid }}>
                  {row.label}
                </div>
                {row.values.map((v, j) => (
                  <div
                    key={j}
                    className="p-4 text-sm font-semibold tabular-nums"
                    style={{ background: C.paper, color: v === "N/A" ? `${C.grid}77` : C.forest }}
                  >
                    {v}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 max-w-2xl">
            <StatusBadge>Pricing FAQ</StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
              Quick answers.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px" style={{ background: C.hairline }}>
            {faqs.map((f, i) => (
              <div key={f.q} className="p-7 md:p-8" style={{ background: C.paper }}>
                <h3 className="mt-2 text-lg font-bold leading-tight" style={{ color: C.forest }}>
                  {f.q}
                </h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* TRUST CTA */}
      <section className="py-20 md:py-24">
        <PageContainer>
          <div
            className="relative border p-10 md:p-14"
            style={{ borderColor: C.hairline, background: C.paper }}
          >
            <CornerMarkers color={C.forest} />

            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <StatusBadge>Get started</StatusBadge>
                <h2 className="mt-4 text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
                  Start with the trial.
                  <br />
                  <span style={{ color: `${C.forest}80` }}>Switch the bill later.</span>
                </h2>
                <p className="mt-4 text-base md:text-lg max-w-xl" style={{ color: C.grid }}>
                  No credit card. No autobill ambush. Talk to a human if you want a
                  walk-through tailored to your team.
                </p>
              </div>
              <div className="md:col-span-5 flex flex-wrap items-center gap-3 md:justify-end">
                <Link to={createPageUrl("Contact")}>
                  <PrimaryButton>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Book a demo
                  </PrimaryButton>
                </Link>
                <Link to={createPageUrl("Products")}>
                  <button
                    type="button"
                    className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center hover:bg-[#1A3C2B]/[0.04] transition-colors duration-150"
                    style={{ borderColor: C.forest, color: C.forest }}
                  >
                    Tour the products
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </Link>
              </div>
            </div>

            <div
              className="mt-8 pt-6 border-t flex items-center justify-between flex-wrap gap-3"
              style={{ borderColor: C.hairline }}
            >
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" style={{ color: C.forest }} />
                <TechLabel style={{ color: `${C.grid}99` }}>
                  SOC 2 · GDPR-ready · Bank-grade encryption
                </TechLabel>
              </div>
              <TechLabel style={{ color: `${C.grid}99` }}>
                pricing in USD · charged monthly · taxes excluded
              </TechLabel>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */
function ToggleButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer px-5 py-2 text-xs font-bold uppercase transition-colors duration-150 inline-flex items-center"
      style={{
        letterSpacing: "0.18em",
        background: active ? C.forest : "transparent",
        color: active ? C.paper : `${C.grid}cc`,
        boxShadow: active
          ? "inset 0 1px 0 rgba(255,255,255,0.10), 0 1px 0 rgba(20,49,36,0.4)"
          : "none",
      }}
    >
      {children}
    </button>
  );
}

function PlanCard({ plan, billing, index }) {
  const price = billing === "monthly" ? plan.monthly : plan.annual;
  const isPopular = plan.popular;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="relative h-full"
    >
      {isPopular && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 z-10"
          style={{ background: C.mint, color: C.forest }}
        >
          <TechLabel>Most popular</TechLabel>
        </div>
      )}

      <div
        className={`ds-spatial relative h-full p-7 md:p-8 flex flex-col ${
          isPopular ? "ds-glass" : ""
        }`}
        style={
          isPopular
            ? { borderLeft: `4px solid ${plan.accent}` }
            : {
                background: C.paper,
                border: `1px solid ${C.hairline}`,
                borderLeft: `4px solid ${plan.accent}`,
              }
        }
      >
        <div className="flex items-center justify-between mb-5">
          <div
            className="w-11 h-11 flex items-center justify-center"
            style={{ background: C.forest, color: C.paper }}
          >
            <plan.icon className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <TechLabel style={{ color: plan.accent }}>{plan.code}</TechLabel>
        </div>

        <TechLabel style={{ color: `${C.grid}99` }}>{plan.name}</TechLabel>

        <div className="mt-3">
          {price !== null ? (
            <div>
              {plan.listMonthly && billing === "monthly" && (
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className="text-base font-semibold line-through tabular-nums"
                    style={{ color: `${C.grid}80` }}
                  >
                    ${plan.listMonthly}
                  </span>
                  <span
                    className="px-1.5 py-0.5 text-[9px] font-bold"
                    style={{ background: C.mint, color: C.forest, letterSpacing: "0.14em" }}
                  >
                    -{Math.round((1 - price / plan.listMonthly) * 100)}%
                  </span>
                </div>
              )}
              <div className="flex items-baseline gap-1.5">
                <span
                  className="text-5xl md:text-6xl font-extrabold tracking-tight tabular-nums"
                  style={{ color: C.forest }}
                >
                  ${price}
                </span>
                <span className="text-sm font-medium" style={{ color: C.grid }}>
                  /month
                </span>
              </div>
            </div>
          ) : (
            <span
              className="text-4xl md:text-5xl font-extrabold tracking-tight"
              style={{ color: C.forest }}
            >
              Custom
            </span>
          )}
          {price !== null && billing === "annual" && (
            <p className="mt-1 text-xs" style={{ color: `${C.grid}cc` }}>
              billed annually · save 20%
            </p>
          )}
        </div>

        <p className="mt-4 text-sm leading-relaxed" style={{ color: C.grid }}>
          {plan.blurb}
        </p>

        <ul className="mt-6 mb-8 space-y-2.5 flex-1">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: C.forest }}>
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: plan.accent }} strokeWidth={2} />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <Link to={plan.href}>
          {isPopular ? (
            <PrimaryButton className="w-full">
              {plan.cta}
              <ArrowRight className="w-4 h-4 ml-2" />
            </PrimaryButton>
          ) : (
            <button
              type="button"
              className="cursor-pointer h-12 px-6 w-full text-sm font-semibold border bg-transparent inline-flex items-center justify-center hover:bg-[#1A3C2B]/[0.04] transition-colors duration-150"
              style={{ borderColor: C.forest, color: C.forest }}
            >
              {plan.cta}
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          )}
        </Link>
      </div>
    </motion.div>
  );
}
